import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import WildQuestLogo from './WildQuestLogo';

/**
 * <DailyIntroSting>
 *
 * A single, brief animated logo reveal the first time an explorer
 * launches the app on a given calendar day. Restores the sense of
 * "entering a curated world" without becoming a splash screen the
 * user has to sit through on every navigation.
 *
 * Behaviour
 * ---------
 * - Localstorage key `wq:intro:lastShown` stores the YYYY-MM-DD of
 *   the last time we played the sting.
 * - If it doesn't match today's date, we play once, then update.
 * - Respects `prefers-reduced-motion` — skipped entirely for users
 *   who've asked for less motion.
 * - Total timeline ~1600 ms: 400 ms reveal in, 800 ms hold, 400 ms out.
 * - Only mounts once per session — after playing, it unmounts so it
 *   never interferes with subsequent route changes.
 */
const STORAGE_KEY = 'wq:intro:lastShown';

function _todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function _shouldPlay() {
  if (typeof window === 'undefined') return false;
  try {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return false;
    return window.localStorage.getItem(STORAGE_KEY) !== _todayIso();
  } catch { return false; }
}

export default function DailyIntroSting() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!_shouldPlay()) return;
    setVisible(true);
    // Stamp storage immediately so a fast reload / route change
    // won't queue a second sting.
    try { window.localStorage.setItem(STORAGE_KEY, _todayIso()); } catch {}
    // Hold + fade out.
    const t = setTimeout(() => setVisible(false), 1200);
    return () => clearTimeout(t);
  }, []);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="daily-intro-sting"
          data-testid="daily-intro-sting"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="fixed inset-0 z-[300] pointer-events-none flex items-center justify-center"
          style={{
            background:
              'radial-gradient(ellipse at 50% 50%, #082720 0%, #040A08 55%, #010604 100%)',
          }}
        >
          {/* Grain overlay — very faint, adds "cinema" quality */}
          <div className="absolute inset-0 opacity-[0.07] mix-blend-overlay"
               style={{
                 backgroundImage:
                   'radial-gradient(circle at 25% 30%, rgba(255,255,255,0.7) 1px, transparent 1px),' +
                   'radial-gradient(circle at 75% 65%, rgba(255,255,255,0.6) 1px, transparent 1px)',
                 backgroundSize: '3px 3px, 5px 5px',
               }} />

          <div className="flex flex-col items-center gap-4 px-8 text-center">
            <motion.div
              initial={{ scale: 0.6, opacity: 0, filter: 'blur(8px)' }}
              animate={{ scale: 1, opacity: 1, filter: 'blur(0px)' }}
              transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
              style={{ filter: 'drop-shadow(0 12px 32px rgba(45,106,79,0.55))' }}
            >
              <WildQuestLogo className="w-28 h-28" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, delay: 0.25 }}
              className="text-center"
            >
              <p className="font-serif text-3xl tracking-tight" style={{ color: '#F1F5F0' }}>
                WildQuest
              </p>
              <p className="mt-1 text-[10px] uppercase tracking-[0.35em]"
                 style={{ color: '#E9C46A' }}>
                Explore · Discover · Protect
              </p>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
